import { useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { TableRow, TableCell } from '@mui/material';
import CustomButton from '../reusable/CustomButton';
import CustomTable, { TableHeaderData } from '../reusable/CustomTable';
import InlineEditText from '../reusable/InlineEditText';
import useUserDetails from '../hooks/useUserDetails';
import {
  SurveyQuestionDetailResponse,
  SurveyTextUpdatedMessage,
  updateQuestionText,
  updateAnswerText,
} from '../../services/surveyService';

interface SurveyQuestionsListProps {
  questions: SurveyQuestionDetailResponse[];
  onDeleteQuestion: (questionId: string, questionText: string) => void;
  eoaSurveyId: string;
  canEdit: boolean;
  surveyUpdatedMessage?: SurveyTextUpdatedMessage | null;
}

interface GroupedAnswer {
  id: string;
  external_key: string;
  text: string;
  sort_order: number;
}

interface GroupedTranslation {
  text: string;
  language_id: string;
  answers: GroupedAnswer[];
}

interface GroupedQuestion {
  id: string;
  display_order: number;
  external_key: string;
  type: string;
  report_display_text: string | null;
  text: string;
  language_id: string;
  answers: GroupedAnswer[];
  translations: { [languageName: string]: GroupedTranslation };
}

const languageNames: { [key: string]: string } = {
  en_en: 'English',
  es_es: 'Spanish',
  pl_pl: 'Polish',
};

const questionPendingKey = (eoaSurveyId: string, questionId: string) => `q:${eoaSurveyId}:${questionId}`;
const answerPendingKey = (eoaSurveyId: string, questionId: string, answerId: string) =>
  `a:${eoaSurveyId}:${questionId}:${answerId}`;

const SurveyQuestionsList = ({
  questions,
  onDeleteQuestion,
  eoaSurveyId,
  canEdit,
  surveyUpdatedMessage,
}: SurveyQuestionsListProps) => {
  const {
    userDetails: { data: userDetails },
  } = useUserDetails();

  const [expandedTranslations, setExpandedTranslations] = useState<Set<string>>(new Set());
  const pendingResolvesRef = useRef<Record<string, (success: boolean) => void>>({});

  useEffect(() => {
    if (!surveyUpdatedMessage) return;

    const msg = surveyUpdatedMessage;
    const pendingKey =
      msg.type === 'question_text_updated'
        ? questionPendingKey(msg.eoa_survey_id, msg.question_id)
        : answerPendingKey(msg.eoa_survey_id, msg.question_id, msg.answer_id ?? '');

    const resolve = pendingResolvesRef.current[pendingKey];
    if (!resolve) return;

    delete pendingResolvesRef.current[pendingKey];
    resolve(msg.success);
  }, [surveyUpdatedMessage]);

  const makeSave = useCallback(
    (pendingKey: string, apiFn: () => Promise<void>): Promise<boolean> =>
      new Promise<boolean>((resolve) => {
        pendingResolvesRef.current[pendingKey] = resolve;
        apiFn().catch(() => {
          delete pendingResolvesRef.current[pendingKey];
          resolve(false);
        });
      }),
    []
  );

  const groupedQuestions = useMemo(() => {
    const grouped: { [key: string]: GroupedQuestion } = {};

    questions.forEach((question) => {
      const key = question.id;

      if (!grouped[key]) {
        if (question.language_code === 'en_en') {
          grouped[key] = {
            id: question.id,
            display_order: question.display_order,
            external_key: question.external_key,
            type: question.type,
            report_display_text: question.report_display_text,
            text: question.text,
            language_id: question.language_id,
            answers: question.answers,
            translations: {},
          };
        }
      } else {
        if (question.language_code !== 'en_en') {
          const languageName = languageNames[question.language_code] || question.language_code;
          grouped[key].translations[languageName] = {
            text: question.text,
            language_id: question.language_id,
            answers: question.answers,
          };
        }
      }
    });

    questions.forEach((question) => {
      const key = question.id;
      if (!grouped[key] && question.language_code !== 'en_en') {
        const languageName = languageNames[question.language_code] || question.language_code;
        grouped[key] = {
          id: question.id,
          display_order: question.display_order,
          external_key: question.external_key,
          type: question.type,
          report_display_text: question.report_display_text,
          text: `[${languageName}] ${question.text}`,
          language_id: question.language_id,
          answers: question.answers,
          translations: {},
        };
      }
    });

    return Object.values(grouped).sort((a, b) => a.display_order - b.display_order);
  }, [questions]);

  const toggleTranslations = (questionId: string) => {
    setExpandedTranslations((prev) => {
      const next = new Set(prev);
      next.has(questionId) ? next.delete(questionId) : next.add(questionId);
      return next;
    });
  };

  if (!questions || questions.length === 0) {
    return <p className="survey-management-no-questions">No questions found for this survey.</p>;
  }

  const tableHeaderData: TableHeaderData<any>[] = [
    { key: 'order', label: '#' },
    { key: 'question', label: 'Question' },
    { key: 'type', label: 'Type' },
    { key: 'actions', label: 'Actions' },
  ];

  return (
    <CustomTable tableHeaderData={tableHeaderData}>
      {groupedQuestions.map((question) => (
        <TableRow key={question.id}>
          <TableCell sx={{ width: '60px' }}>{question.display_order}</TableCell>
          <TableCell>
            <div className="survey-management-question-cell">
              <div className="survey-management-question-text">
                <InlineEditText
                  text={question.text}
                  disabled={!canEdit}
                  onSave={(newText) =>
                    userDetails
                      ? makeSave(questionPendingKey(eoaSurveyId, question.id), () =>
                          updateQuestionText(
                            eoaSurveyId,
                            question.id,
                            { language_id: question.language_id, text: newText },
                            userDetails.access_token
                          )
                        )
                      : Promise.resolve(false)
                  }
                />
              </div>
              {question.report_display_text && question.report_display_text !== question.text && (
                <div className="survey-management-question-report-text">
                  Report display: {question.report_display_text}
                </div>
              )}
              {question.answers && question.answers.length > 0 && (
                <div className="survey-management-question-answers">
                  <strong>Answers:</strong>
                  <ul>
                    {[...question.answers]
                      .sort((a, b) => a.sort_order - b.sort_order)
                      .map((answer) => (
                        <li key={answer.id}>
                          <InlineEditText
                            text={answer.text}
                            disabled={!canEdit}
                            onSave={(newText) =>
                              userDetails
                                ? makeSave(answerPendingKey(eoaSurveyId, question.id, answer.id), () =>
                                    updateAnswerText(
                                      eoaSurveyId,
                                      question.id,
                                      answer.id,
                                      { language_id: question.language_id, text: newText },
                                      userDetails.access_token
                                    )
                                  )
                                : Promise.resolve(false)
                            }
                          />
                        </li>
                      ))}
                  </ul>
                </div>
              )}
              {Object.keys(question.translations).length > 0 && (
                <div className="survey-management-question-translations">
                  <CustomButton
                    variant="text"
                    buttonText={expandedTranslations.has(question.id) ? '▼ Hide translations' : '▶ Show translations'}
                    buttonAction={() => toggleTranslations(question.id)}
                    sx={{ fontSize: 'var(--font-size-12)', padding: '4px 0', minWidth: 0, color: 'var(--text-gray)' }}
                  />
                  {expandedTranslations.has(question.id) && (
                    <div className="survey-management-translations-list">
                      {Object.entries(question.translations).map(([language, translation]) => (
                        <div key={language} className="survey-management-translation-item">
                          <div className="survey-management-translation-header">
                            <span className="survey-management-translation-flag">{language}:</span>
                            <span className="survey-management-translation-text">
                              <InlineEditText
                                text={translation.text}
                                disabled={!canEdit}
                                onSave={(newText) =>
                                  userDetails
                                    ? makeSave(questionPendingKey(eoaSurveyId, question.id), () =>
                                        updateQuestionText(
                                          eoaSurveyId,
                                          question.id,
                                          { language_id: translation.language_id, text: newText },
                                          userDetails.access_token
                                        )
                                      )
                                    : Promise.resolve(false)
                                }
                              />
                            </span>
                          </div>
                          {translation.answers && translation.answers.length > 0 && (
                            <div className="survey-management-question-answers">
                              <strong>Answers:</strong>
                              <ul>
                                {[...translation.answers]
                                  .sort((a: any, b: any) => a.sort_order - b.sort_order)
                                  .map((answer: any) => (
                                    <li key={answer.id}>
                                      <InlineEditText
                                        text={answer.text}
                                        disabled={!canEdit}
                                        onSave={(newText) =>
                                          userDetails
                                            ? makeSave(answerPendingKey(eoaSurveyId, question.id, answer.id), () =>
                                                updateAnswerText(
                                                  eoaSurveyId,
                                                  question.id,
                                                  answer.id,
                                                  { language_id: translation.language_id, text: newText },
                                                  userDetails.access_token
                                                )
                                              )
                                            : Promise.resolve(false)
                                        }
                                      />
                                    </li>
                                  ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          </TableCell>
          <TableCell sx={{ width: '120px' }}>{question.type}</TableCell>
          <TableCell sx={{ width: '120px' }}>
            <CustomButton
              variant="text"
              buttonText="Delete"
              buttonAction={() => onDeleteQuestion(question.id, question.text)}
              disabled={!canEdit}
              sx={{ color: 'var(--error-main)', fontSize: 'var(--font-size-14)', padding: 0, minWidth: 0 }}
            />
          </TableCell>
        </TableRow>
      ))}
    </CustomTable>
  );
};

export default SurveyQuestionsList;
